import { MapPinIcon } from "@heroicons/react/24/outline";
import { FC, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import __cities from "../../data/jsons/cities.json";

export interface InputFieldProps {
  placeHolder?: string;
  desc?: string;
  className?: string;
  divHideVerticalLineClass?: string;
  autoFocus?: boolean;
  onChange?: (value: string) => void;
  onSelectEvent?: (value: string) => void;
  clear: boolean;
  propEventValue?: string | null;
  name?: string;
  onInputChange?: (value: string) => void;
  placePredictions?: google.maps.places.AutocompletePrediction[];
  isPlacePredictionsLoading?: boolean;
  onSelectPrediction?: (placeId: string) => void;
}

const InputField: FC<InputFieldProps> = ({
  autoFocus = false,
  placeHolder = "text",
  desc = "Saisissez votre texte",
  className = "nc-flex-1.5",
  divHideVerticalLineClass = "left-10 -right-0.5",
  onSelectEvent,
  onInputChange,
  name = "text",
  clear,
  propEventValue,
  placePredictions = [],
  isPlacePredictionsLoading = false,
  onSelectPrediction,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(propEventValue || "");
  const [showPopover, setShowPopover] = useState(autoFocus);

  useEffect(() => {
    setShowPopover(autoFocus);
  }, [autoFocus]);

  useEffect(() => {
    if (clear) {
      setValue("");
    }
  }, [clear]);

  useEffect(() => {
    setValue(propEventValue || "");
  }, [propEventValue]);

  const eventClickOutsideDiv = (event: MouseEvent) => {
    if (!containerRef.current) return;
    if (!showPopover || containerRef.current.contains(event.target as Node)) {
      return;
    }
    setShowPopover(false);
    // Supprimé le refocus automatique qui interfère avec la saisie
  };

  useEffect(() => {
    if (showPopover) {
      document.addEventListener("click", eventClickOutsideDiv);
    }
    return () => {
      document.removeEventListener("click", eventClickOutsideDiv);
    };
  }, [showPopover]);

  useEffect(() => {
    if (showPopover && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showPopover]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    setValue(newValue);
    onSelectEvent?.(newValue);
    onInputChange?.(newValue);

    // Ouvrir le popover seulement quand on tape (pas sur focus)
    setShowPopover(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Gérer la suppression avec Escape pour fermer le dropdown
    if (e.key === "Escape") {
      setShowPopover(false);
    }

    // Gérer la suppression manuelle pour Backspace et Delete
    if (e.key === "Backspace" || e.key === "Delete") {
      // Laisser l'événement se propager normalement
      setShowPopover(true);
    }

    // Gérer Enter pour sélectionner la première suggestion
    if (e.key === "Enter" && showPopover) {
      const filteredCities = __cities
        .filter(
          (city) =>
            city.label.toLowerCase().startsWith(value.toLowerCase()) ||
            city.label.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 8);

      if (filteredCities.length > 0) {
        e.preventDefault();
        setValue(filteredCities[0].label);
        onSelectEvent?.(filteredCities[0].label);
        setShowPopover(false);
      }
    }
  };

  const handleSelectPrediction = (prediction: google.maps.places.AutocompletePrediction) => {
    const newValue = prediction.description.replace(/,\s*France$/, "").trim();
    setValue(newValue);
    onSelectEvent?.(newValue);
    onSelectPrediction?.(prediction.place_id);
    setShowPopover(false);
  };

  const formatPredictionDescription = (description: string) => {
    return description.replace(/,\s*France$/, "").trim();
  };

  return (
    <div className={`flex relative ${className}`} ref={containerRef}>
      <div
        onClick={() => setShowPopover(true)}
        className={`flex z-10 flex-1 relative [ nc-hero-field-padding ] flex-shrink-0 items-center space-x-3 cursor-pointer focus:outline-none text-left ${
          showPopover ? "nc-hero-field-focused" : ""
        }`}
      >
        <div className="text-neutral-300 dark:text-neutral-400">
          <MapPinIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow">
          <input
            name={name}
            autoComplete="off"
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleInputChange}
            onInput={handleInputChange}
            onKeyDown={handleKeyDown}
            onKeyUp={(e) => console.log("🔑 KeyUp:", e.key, "Value:", e.currentTarget.value)}
            onFocus={() => console.log("🎯 Input focused")}
            onBlur={() => console.log("🎯 Input blurred")}
            className="block font-semibold truncate bg-transparent border-none focus:outline-none focus:ring-0 focus:bg-transparent focus:placeholder-neutral-300 xl:text-lg placeholder-neutral-800 dark:placeholder-neutral-200"
            placeholder={placeHolder}
            autoFocus={showPopover}
          />
          <span className="block mt-0.5 text-sm text-neutral-400 font-light">
            <span>{value ? placeHolder : desc}</span>
          </span>
        </div>
      </div>

      {showPopover && (
        <ul className="absolute z-20 w-full mt-12 overflow-y-auto bg-white border rounded-lg shadow-lg dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 max-h-60">
          {value
            ? // Afficher les suggestions filtrées si il y a du texte
              __cities
                .filter(
                  (city) =>
                    city.label.toLowerCase().startsWith(value.toLowerCase()) ||
                    city.label.toLowerCase().includes(value.toLowerCase())
                )
                .slice(0, 8)
                .map((city) => (
                  <li
                    key={city.label}
                    onClick={() => {
                      setValue(city.label);
                      onSelectEvent?.(city.label);
                      setShowPopover(false);
                    }}
                    className="px-4 py-2 cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                  >
                    {city.label}
                  </li>
                ))
            : // Afficher les villes populaires si le champ est vide
              __cities.slice(0, 10).map((city) => (
                <li
                  key={city.label}
                  onClick={() => {
                    setValue(city.label);
                    onSelectEvent?.(city.label);
                    setShowPopover(false);
                  }}
                  className="px-4 py-2 cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                >
                  {city.label}
                </li>
              ))}
        </ul>
      )}

      <AnimatePresence>
        {showPopover && isPlacePredictionsLoading && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-20 flex items-center justify-center w-full p-4 mt-12 space-x-2 bg-white border rounded-lg shadow-lg dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700"
          >
            <svg
              className="w-5 h-5 text-blue-500 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="font-medium text-neutral-600 dark:text-neutral-300">Chargement...</span>
          </motion.div>
        )}
      </AnimatePresence>

      {showPopover && (
        <div
          className={`absolute top-1/2 z-0 self-center h-8 bg-white -translate-y-1/2 dark:bg-neutral-800 ${divHideVerticalLineClass}`}
        ></div>
      )}
    </div>
  );
};

export default InputField;
